import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import DirectCalendarWidget from "../../components/direct/DirectCalendarWidget";
import { getWidgetConfiguration } from "../../../api";
import "../wrapper/StatsBuilderLayout.css";
import Footer from "../footer/Footer";

const DISCIPLINE_OPTIONS = [
  { value: "rugby", label: "Rugby" },
  { value: "soccer", label: "Football" },
];

const clampCompetitionIds = (value = "") =>
  String(value || "")
    .split(",")
    .map((id) => id.trim())
    .filter(Boolean);

export default function DirectCalendarPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const widgetConfigurationId = searchParams.get("widgetConfigurationId") || null;
  const dateParam = searchParams.get("date") || null;

  const disciplineParam = searchParams.get("discipline") || "";
  const hasDisciplineParam = searchParams.has("discipline");

  const competitionIdsParam = searchParams.get("competitionIds") || "";
  const competitionIds = clampCompetitionIds(competitionIdsParam);
  const hasCompetitionIdsParam = searchParams.has("competitionIds");

  const [widgetConfig, setWidgetConfig] = useState(null);
  const [widgetConfigLoading, setWidgetConfigLoading] = useState(false);
  const [widgetConfigError, setWidgetConfigError] = useState("");
  const [reloadKey, setReloadKey] = useState(0);
  const [competitionFilterPortalTarget, setCompetitionFilterPortalTarget] = useState(null);
  const [showCompetitionFilter, setShowCompetitionFilter] = useState(false);

  useEffect(() => {
    let ignore = false;

    const loadWidgetConfig = async () => {
      if (!widgetConfigurationId) {
        setWidgetConfig(null);
        setWidgetConfigError("widgetConfigurationId est requis.");
        return;
      }

      try {
        setWidgetConfigLoading(true);
        setWidgetConfigError("");

        const { data } = await getWidgetConfiguration(widgetConfigurationId);

        if (ignore) return;

        if (!data) {
          setWidgetConfig(null);
          setWidgetConfigError("Configuration widget introuvable.");
          return;
        }

        if (data.widgetType !== "direct_calendar") {
          setWidgetConfig(null);
          setWidgetConfigError("Cette configuration n'est pas un widget calendrier direct.");
          return;
        }

        setWidgetConfig(data);
      } catch {
        if (!ignore) {
          setWidgetConfig(null);
          setWidgetConfigError("Impossible de charger la configuration du widget calendrier.");
        }
      } finally {
        if (!ignore) {
          setWidgetConfigLoading(false);
        }
      }
    };

    loadWidgetConfig();

    return () => {
      ignore = true;
    };
  }, [widgetConfigurationId]);

  const allowedDisciplines = useMemo(() => {
    if (Array.isArray(widgetConfig?.disciplines) && widgetConfig.disciplines.length) {
      return widgetConfig.disciplines;
    }

    if (widgetConfig?.discipline) {
      return [widgetConfig.discipline];
    }

    return [];
  }, [widgetConfig]);

  const effectiveDiscipline = useMemo(() => {
    if (!allowedDisciplines.length) {
      return disciplineParam || "";
    }

    if (hasDisciplineParam) {
      return allowedDisciplines.includes(disciplineParam) ? disciplineParam : "";
    }

    if (
      widgetConfig?.defaultDiscipline &&
      allowedDisciplines.includes(widgetConfig.defaultDiscipline)
    ) {
      return widgetConfig.defaultDiscipline;
    }

    if (allowedDisciplines.length === 1) {
      return allowedDisciplines[0];
    }

    return "";
  }, [allowedDisciplines, disciplineParam, hasDisciplineParam, widgetConfig]);

  const showDisciplineInput = allowedDisciplines.length > 1;

  const handleCompetitionFilterVisibilityChange = useCallback((visible) => {
    setShowCompetitionFilter(Boolean(visible));
  }, []);

  const handleDisciplineChange = (event) => {
    const nextDiscipline = event.target.value;
    const nextParams = new URLSearchParams(searchParams);

    nextParams.delete("date");
    nextParams.delete("competitionIds");

    if (nextDiscipline) {
      nextParams.set("discipline", nextDiscipline);
    } else {
      // important : discipline= signifie choix utilisateur explicite "tous les sports"
      nextParams.set("discipline", "");
    }

    if (widgetConfigurationId) {
      nextParams.set("widgetConfigurationId", widgetConfigurationId);
    }

    setSearchParams(nextParams, { replace: true });
  };

  const handleDateChange = (nextDate) => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("date", nextDate);

    if (widgetConfigurationId) {
      nextParams.set("widgetConfigurationId", widgetConfigurationId);
    }

    setSearchParams(nextParams, { replace: true });
  };

  const handleCompetitionIdsChange = (nextCompetitionIds = []) => {
    const nextParams = new URLSearchParams(searchParams);

    nextParams.delete("date");

    if (Array.isArray(nextCompetitionIds) && nextCompetitionIds.length) {
      nextParams.set("competitionIds", nextCompetitionIds.join(","));
    } else {
      nextParams.set("competitionIds", "");
    }

    if (widgetConfigurationId) {
      nextParams.set("widgetConfigurationId", widgetConfigurationId);
    }

    setSearchParams(nextParams, { replace: true });
  };

  const shouldShowTitleHeader = Boolean(widgetConfig?.configName);

  const shouldShowFilterPanel =
    widgetConfigLoading || widgetConfigError || showDisciplineInput || showCompetitionFilter;

  return (
    <Box
      className="stats-builder-page stats-builder-page--direct-calendar"
      sx={{
        width: "100%",
        maxWidth: "100%",
        boxSizing: "border-box",
        px: { xs: 1.5, sm: 2, md: 3 },
        py: { xs: 2, md: 3 },
        bgcolor: "#fff",
      }}
    >
      <Box sx={{ maxWidth: 1180, mx: "auto" }} className="stats-builder-page__container">
        <Stack spacing={3} className="stats-builder-page__stack">
          {shouldShowTitleHeader ? (
            <Box className="stats-builder__header">
              <Box className="stats-builder__title-wrap">
                <span className="stats-builder__title-dot" aria-hidden="true" />
                <Typography variant="h5" className="stats-builder__title">
                  {widgetConfig.configName}
                </Typography>
              </Box>
            </Box>
          ) : null}

          {shouldShowFilterPanel ? (
            <Paper
              elevation={0}
              variant="outlined"
              className="stats-builder-panel"
              sx={{
                borderRadius: 3,
                borderColor: "#e5e7eb",
                p: { xs: 2, md: 3 },
              }}
            >
              <Stack spacing={2} className="stats-builder-panel__content_inner">
                <Box className="stats-builder-panel__content stats-builder-panel__content--filters">
                  {widgetConfigLoading ? (
                    <Box className="stats-builder-loader">
                      <CircularProgress size={24} />
                    </Box>
                  ) : null}

                  {widgetConfigError ? (
                    <Alert severity="error">{widgetConfigError}</Alert>
                  ) : null}

                  {showDisciplineInput ? (
                    <Box className="stats-builder__filter-input">
                      <p>Sport</p>
                      <TextField
                        select
                        fullWidth
                        value={effectiveDiscipline}
                        onChange={handleDisciplineChange}
                        SelectProps={{
                          displayEmpty: true,
                        }}
                      >
                        <MenuItem value="" className="stats-builder-menu-item">
                          <em>Tous les sports</em>
                        </MenuItem>

                        {DISCIPLINE_OPTIONS
                          .filter((option) => allowedDisciplines.includes(option.value))
                          .map((option) => (
                            <MenuItem
                              key={option.value}
                              value={option.value}
                              className="stats-builder-menu-item"
                            >
                              {option.label}
                            </MenuItem>
                          ))}
                      </TextField>
                    </Box>
                  ) : null}

                  <Box
                    ref={setCompetitionFilterPortalTarget}
                    sx={{ display: "contents" }}
                  />
                </Box>
              </Stack>
            </Paper>
          ) : null}

          <DirectCalendarWidget
            widgetConfigurationId={widgetConfigurationId}
            date={dateParam}
            discipline={effectiveDiscipline || null}
            competitionIds={competitionIds}
            hasCompetitionIdsParam={hasCompetitionIdsParam}
            reloadKey={reloadKey}
            onRefresh={() => setReloadKey((value) => value + 1)}
            refreshDisabled={widgetConfigLoading || Boolean(widgetConfigError)}
            competitionFilterPlacement="portal"
            competitionFilterPortalTarget={competitionFilterPortalTarget}
            onCompetitionFilterVisibilityChange={handleCompetitionFilterVisibilityChange}
            onCompetitionIdsChange={handleCompetitionIdsChange}
            onDateChange={handleDateChange}
            onNavigate={(url) => navigate(url)}
          />
        </Stack>
      </Box>

      <Footer isDSAS logoSize="l" align="center" />
               
    </Box>
  );
}
